// Runtime eyes utilities used by both monolithic and modular engines
import { EYE_LAYER_POLYGONS, IRIS_INDICES } from '../avatarInspector/featureIndices'

const clamp = (value, min, max) => Math.max(min, Math.min(max, value))
const clampByte = (v) => clamp(Math.round(v), 0, 255)
const rgba = (r, g, b, a = 0.96) =>
  `rgba(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)}, ${a})`
const eyeVisibilityFromBlink = (blink) => {
  const b = clamp(Number(blink) || 0, 0, 1)
  if (b >= 0.46) return 0
  return Math.pow(1 - b, 2.2)
}
const toOpaqueColor = (value, fallback = 'rgba(24, 20, 28, 1)') => {
  const raw = typeof value === 'string' ? value.trim() : ''
  if (!raw) return fallback

  const rgbaMatch = raw.match(
    /^rgba?\(\s*([0-9.+-]+)\s*,\s*([0-9.+-]+)\s*,\s*([0-9.+-]+)(?:\s*,\s*[0-9.+-]+)?\s*\)$/i
  )
  if (rgbaMatch) {
    const r = clampByte(Number(rgbaMatch[1]) || 0)
    const g = clampByte(Number(rgbaMatch[2]) || 0)
    const b = clampByte(Number(rgbaMatch[3]) || 0)
    return `rgba(${r}, ${g}, ${b}, 1)`
  }

  const hex = raw.replace('#', '')
  if (/^[0-9a-f]{3,4}$/i.test(hex)) {
    const r = parseInt(hex[0] + hex[0], 16)
    const g = parseInt(hex[1] + hex[1], 16)
    const b = parseInt(hex[2] + hex[2], 16)
    return `rgba(${r}, ${g}, ${b}, 1)`
  }
  if (/^[0-9a-f]{6,8}$/i.test(hex)) {
    const r = parseInt(hex.slice(0, 2), 16)
    const g = parseInt(hex.slice(2, 4), 16)
    const b = parseInt(hex.slice(4, 6), 16)
    return `rgba(${r}, ${g}, ${b}, 1)`
  }

  return raw
}

const withForcedOpaqueAlpha = (rgbaString, minAlpha = 0.98) => {
  const raw = typeof rgbaString === 'string' ? rgbaString.trim() : ''
  const m = raw.match(
    /^rgba?\(\s*([0-9.+-]+)\s*,\s*([0-9.+-]+)\s*,\s*([0-9.+-]+)(?:\s*,\s*([0-9.+-]+))?\s*\)$/i
  )
  if (!m) return raw
  const r = clampByte(Number(m[1]) || 0)
  const g = clampByte(Number(m[2]) || 0)
  const b = clampByte(Number(m[3]) || 0)
  const a = Number.isFinite(Number(m[4])) ? Number(m[4]) : 1
  return `rgba(${r}, ${g}, ${b}, ${Math.max(minAlpha, clamp(a, 0, 1))})`
}

export const clipToEyeSockets = (ctx, points) => {
  if (!ctx || !points?.length) return false
  let hasPath = false
  ctx.beginPath()
  const buildPath = (indices) => {
    if (!indices?.length) return
    const first = points[indices[0]]
    if (!first) return
    ctx.moveTo(first.x, first.y)
    for (let i = 1; i < indices.length; i += 1) {
      const p = points[indices[i]]
      if (!p) continue
      ctx.lineTo(p.x, p.y)
    }
    ctx.closePath()
    hasPath = true
  }
  buildPath(EYE_LAYER_POLYGONS.left)
  buildPath(EYE_LAYER_POLYGONS.right)
  if (hasPath) ctx.clip()
  return hasPath
}

export const fillEyeSockets = (ctx, points, settings) => {
  if (!ctx || !points?.length) return false
  let hasPath = false

  const drawSocketWithGradient = (indices, side) => {
    if (!indices?.length) return
    let cx = 0
    let cy = 0
    let vcount = 0
    for (let i = 0; i < indices.length; i += 1) {
      const v = points[indices[i]]
      if (!v) continue
      cx += v.x
      cy += v.y
      vcount += 1
    }
    if (!vcount) return
    cx /= vcount
    cy /= vcount
    let r = 0
    for (let i = 0; i < indices.length; i += 1) {
      const v = points[indices[i]]
      if (!v) continue
      r = Math.max(r, Math.hypot(v.x - cx, v.y - cy))
    }

    const inner = Math.max(1, r * 0.35)
    const outer = Math.max(inner + 1, r * 1.02)
    const grad = ctx.createRadialGradient(cx, cy, inner, cx, cy, outer)
    const centerCol = settings?.eye?.scleraCenter || 'rgba(246, 249, 252, 0.98)'
    const edgeCol = settings?.eye?.scleraEdge || 'rgba(215, 221, 230, 0.92)'
    grad.addColorStop(0, centerCol)
    grad.addColorStop(1, edgeCol)

    ctx.save()
    const blinkLeft = clamp(Number(settings?.blink?.left) || 0, 0, 1)
    const blinkRight = clamp(Number(settings?.blink?.right) || 0, 0, 1)
    const sideAlpha = side === 'left' ? eyeVisibilityFromBlink(blinkLeft) : eyeVisibilityFromBlink(blinkRight)
    if (sideAlpha <= 0.02) {
      ctx.restore()
      return
    }
    const prevAlpha = ctx.globalAlpha
    ctx.globalAlpha = prevAlpha * sideAlpha
    ctx.beginPath()
    const first = points[indices[0]]
    if (!first) {
      ctx.restore()
      return
    }
    ctx.moveTo(first.x, first.y)
    for (let i = 1; i < indices.length; i += 1) {
      const p = points[indices[i]]
      if (!p) continue
      ctx.lineTo(p.x, p.y)
    }
    ctx.closePath()
    ctx.fillStyle = grad
    ctx.fill()
    ctx.restore()
    hasPath = true
  }

  drawSocketWithGradient(EYE_LAYER_POLYGONS.left, 'left')
  drawSocketWithGradient(EYE_LAYER_POLYGONS.right, 'right')
  return hasPath
}

export const computeIrisStats = (points, centerIdx, ring) => {
  if (!points?.length || !ring?.length) return null
  let center = points[centerIdx] || null
  if (!center) {
    let sx = 0
    let sy = 0
    let count = 0
    for (let i = 0; i < ring.length; i += 1) {
      const p = points[ring[i]]
      if (!p) continue
      sx += p.x
      sy += p.y
      count += 1
    }
    if (!count) return null
    center = { x: sx / count, y: sy / count }
  }

  let radiusSum = 0
  let radiusCount = 0
  for (let i = 0; i < ring.length; i += 1) {
    const p = points[ring[i]]
    if (!p) continue
    radiusSum += Math.hypot(p.x - center.x, p.y - center.y)
    radiusCount += 1
  }
  const radius = radiusCount ? radiusSum / radiusCount : 2
  return { cx: center.x, cy: center.y, radius: Math.max(1.2, radius * 0.98) }
}

export const drawPupilCircles = (ctx, points, palette = null, settings = {}) => {
  if (!ctx || !points?.length) return false
  const left = computeIrisStats(points, IRIS_INDICES.leftCenter, IRIS_INDICES.leftRing)
  const right = computeIrisStats(points, IRIS_INDICES.rightCenter, IRIS_INDICES.rightRing)
  if (!left && !right) return false

  const defaults = { left: { r: 100, g: 142, b: 186 }, right: { r: 100, g: 142, b: 186 } }
  const irisColorFor = (side) => {
    const src = palette?.[side] || defaults[side]
    return { r: clampByte(src.r), g: clampByte(src.g), b: clampByte(src.b) }
  }

  const hexToRgb = (h) => {
    if (!h || typeof h !== 'string') return null
    const s = h.replace('#', '')
    if (s.length !== 6) return null
    const r = parseInt(s.slice(0, 2), 16)
    const g = parseInt(s.slice(2, 4), 16)
    const b = parseInt(s.slice(4, 6), 16)
    if ([r, g, b].some((v) => !Number.isFinite(v))) return null
    return { r, g, b }
  }

  const drawOne = (stats, side) => {
    if (!stats) return
    const blinkLeft = clamp(Number(settings?.blink?.left) || 0, 0, 1)
    const blinkRight = clamp(Number(settings?.blink?.right) || 0, 0, 1)
    const sideAlpha = side === 'left' ? eyeVisibilityFromBlink(blinkLeft) : eyeVisibilityFromBlink(blinkRight)
    if (sideAlpha <= 0.08) return
    const overrideIris = hexToRgb(settings?.eye?.irisColor) || hexToRgb(settings?.irisColor)
    const iris = overrideIris || irisColorFor(side)
    const baseIris = Math.max(1.3, stats.radius)
    const shrink = 0.6 + sideAlpha * 0.4
    const irisR = Math.max(1.1, baseIris * shrink)
    const pupilR = Math.max(0.8, irisR * 0.52)

    const grad = ctx.createRadialGradient(
      stats.cx - irisR * 0.2,
      stats.cy - irisR * 0.2,
      irisR * 0.12,
      stats.cx,
      stats.cy,
      irisR
    )
    grad.addColorStop(
      0,
      `rgba(${clampByte(iris.r + 55)}, ${clampByte(iris.g + 45)}, ${clampByte(iris.b + 35)}, 0.96)`
    )
    grad.addColorStop(0.62, `rgba(${iris.r}, ${iris.g}, ${iris.b}, 0.98)`)
    grad.addColorStop(
      1,
      `rgba(${clampByte(iris.r * 0.42)}, ${clampByte(iris.g * 0.38)}, ${clampByte(iris.b * 0.35)}, 0.98)`
    )

    ctx.save()
    const prevAlpha = ctx.globalAlpha
    ctx.globalAlpha = prevAlpha * (0.9 * sideAlpha)
    ctx.beginPath()
    ctx.arc(stats.cx, stats.cy, irisR, 0, Math.PI * 2)
    ctx.fillStyle = grad
    ctx.fill()
    if (settings?.eye?.irisBorderColor) {
      ctx.beginPath()
      ctx.arc(stats.cx, stats.cy, irisR, 0, Math.PI * 2)
      ctx.strokeStyle = settings.eye.irisBorderColor
      ctx.lineWidth = Math.max(0.6, irisR * 0.08)
      ctx.stroke()
    }

    ctx.beginPath()
    ctx.arc(stats.cx, stats.cy, pupilR, 0, Math.PI * 2)
    ctx.fillStyle = settings?.eye?.pupilColor || 'rgba(10, 14, 22, 0.98)'
    ctx.fill()

    ctx.beginPath()
    ctx.arc(
      stats.cx - pupilR * 0.26,
      stats.cy - pupilR * 0.3,
      Math.max(0.45, pupilR * 0.22),
      0,
      Math.PI * 2
    )
    ctx.fillStyle = 'rgba(255, 255, 255, 0.55)'
    ctx.fill()
    ctx.restore()
  }

  drawOne(left, 'left')
  drawOne(right, 'right')
  return true
}

// Sample average eyelid color from avatar image within eye polygon area.
export const sampleLidColor = (adapter, side = 'left', avatarImage, avatarRig, avatarKey = '') => {
  try {
    const key = `${avatarKey || 'none'}:${side}`
    // simple in-memory cache stored on avatarRig to avoid global state
    if (avatarRig && avatarRig._lidColorCache && avatarRig._lidColorCache.has(key))
      return avatarRig._lidColorCache.get(key)
    const img = avatarImage?.img
    if (!img || !avatarRig?.pointsPx?.length) throw new Error('no-image')
    const poly = side === 'left' ? EYE_LAYER_POLYGONS?.left || [] : EYE_LAYER_POLYGONS?.right || []
    const pts = poly.map((i) => avatarRig.pointsPx[i]).filter(Boolean)
    if (!pts.length) throw new Error('no-pts')
    let minx = Infinity,
      miny = Infinity,
      maxx = -Infinity,
      maxy = -Infinity
    for (const p of pts) {
      minx = Math.min(minx, p.x)
      miny = Math.min(miny, p.y)
      maxx = Math.max(maxx, p.x)
      maxy = Math.max(maxy, p.y)
    }
    const w0 = Math.max(1, maxx - minx)
    const h0 = Math.max(1, maxy - miny)
    const padX = w0 * 0.45
    const padTop = h0 * 0.7
    const padBot = h0 * 0.25
    const sx = Math.max(0, Math.floor(minx - padX))
    const sy = Math.max(0, Math.floor(miny - padTop))
    const ex = Math.min(Math.floor((img.naturalWidth || img.width) - 1), Math.ceil(maxx + padX))
    const ey = Math.min(Math.floor((img.naturalHeight || img.height) - 1), Math.ceil(maxy + padBot))
    const sw = Math.max(1, ex - sx)
    const sh = Math.max(1, ey - sy)
    const read = adapter.createCanvas(
      img.naturalWidth || img.width,
      img.naturalHeight || img.height
    )
    read.width = img.naturalWidth || img.width
    read.height = img.naturalHeight || img.height
    const rctx = adapter.getContext(read, '2d', { willReadFrequently: true })
    rctx.drawImage(img, 0, 0, read.width, read.height)
    const data = rctx.getImageData(sx, sy, sw, sh).data
    let sr = 0,
      sg = 0,
      sb = 0,
      cnt = 0
    const step = Math.max(1, Math.floor((sw * sh) / 5000))
    for (let i = 0; i < data.length; i += 4 * step) {
      const r = data[i]
      const g = data[i + 1]
      const b = data[i + 2]
      const a = data[i + 3]
      if (a < 8) continue
      sr += r
      sg += g
      sb += b
      cnt += 1
    }
    if (!cnt) throw new Error('empty')
    let r = clampByte(sr / cnt)
    let g = clampByte(sg / cnt)
    let b = clampByte(sb / cnt)
    // Avoid near-white sampled lids (sclera contamination) by capping luminance.
    const luma = 0.2126 * r + 0.7152 * g + 0.0722 * b
    if (luma > 170) {
      const k = 170 / Math.max(1, luma)
      r = clampByte(r * k)
      g = clampByte(g * k)
      b = clampByte(b * k)
    } else if (luma < 72) {
      const k = 72 / Math.max(1, luma)
      r = clampByte(r * k)
      g = clampByte(g * k)
      b = clampByte(b * k)
    }
    const col = rgba(r, g, b)
    if (avatarRig) {
      if (!avatarRig._lidColorCache) avatarRig._lidColorCache = new Map()
      avatarRig._lidColorCache.set(key, col)
    }
    return col
  } catch (e) {
    return 'rgba(24, 20, 28, 0.96)'
  }
}

const easeInOutCubic = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2)

// Draw a realistic eyelid sweep clipped to the eye socket, with a subtle rim seal
export const drawEyelidOccluder = (ctx, points, side, blink, opts = {}) => {
  const { poly, color, start = 0.5, full = 0.85, minPhase = 0.1 } = opts || {}
  if (!ctx || !points?.length || !poly?.length) return false
  const b = clamp((Number(blink) || 0 - start) / Math.max(1e-4, full - start), 0, 1)
  if (b <= 0 || b < minPhase) return false
  const isLeft = side === 'left'
  const LANDMARKS = {
    leftEye: 33,
    rightEye: 263,
    leftEyeInner: 133,
    rightEyeInner: 362,
    leftEyeUpper: 159,
    leftEyeLower: 145,
    rightEyeUpper: 386,
    rightEyeLower: 374
  }
  const a = easeInOutCubic(b)
  const outerIdx = isLeft ? LANDMARKS.leftEye : LANDMARKS.rightEye
  const innerIdx = isLeft ? LANDMARKS.leftEyeInner : LANDMARKS.rightEyeInner
  const upIdx = isLeft ? LANDMARKS.leftEyeUpper : LANDMARKS.rightEyeUpper
  const lowIdx = isLeft ? LANDMARKS.leftEyeLower : LANDMARKS.rightEyeLower
  const pOuter = points[outerIdx]
  const pInner = points[innerIdx]
  const pUp = points[upIdx]
  const pLo = points[lowIdx]
  if (!pOuter || !pInner || !pUp || !pLo) return false

  const polyPts = poly.map((idx) => points[idx]).filter(Boolean)
  if (polyPts.length < 3) return false

  const outerPos = poly.indexOf(outerIdx)
  const innerPos = poly.indexOf(innerIdx)
  if (outerPos < 0 || innerPos < 0) return false

  const ringSliceInclusive = (arr, fromIdx, toIdx, dir = 1) => {
    const out = []
    const n = arr.length
    let i = fromIdx
    let guard = 0
    while (guard < n + 1) {
      out.push(arr[i])
      if (i === toIdx) break
      i = dir > 0 ? (i + 1) % n : (i - 1 + n) % n
      guard += 1
    }
    return out
  }

  const buildChain = (indices) => indices.map((idx) => points[idx]).filter(Boolean)
  const chainFwdIdx = ringSliceInclusive(poly, outerPos, innerPos, 1)
  const chainRevIdx = ringSliceInclusive(poly, outerPos, innerPos, -1)
  const scoreUpperChain = (indices) => {
    if (!indices?.length) return Number.POSITIVE_INFINITY
    let up = 0
    let lo = 0
    let count = 0
    for (let i = 0; i < indices.length; i += 1) {
      const p = points[indices[i]]
      if (!p) continue
      up += Math.hypot(p.x - pUp.x, p.y - pUp.y)
      lo += Math.hypot(p.x - pLo.x, p.y - pLo.y)
      count += 1
    }
    if (!count) return Number.POSITIVE_INFINITY
    return up / count - lo / count
  }
  const useFwdAsUpper = scoreUpperChain(chainFwdIdx) <= scoreUpperChain(chainRevIdx)
  const upperChainIdx = useFwdAsUpper ? chainFwdIdx : chainRevIdx
  const lowerChainIdx = useFwdAsUpper ? chainRevIdx : chainFwdIdx
  const upperChain = buildChain(upperChainIdx)
  const lowerChain = buildChain(lowerChainIdx)
  if (upperChain.length < 2 || lowerChain.length < 2) return false

  // Blink by morphing the upper perimeter arc toward the lower perimeter arc.
  // This keeps closure locked to the eye socket boundary and avoids deformation artifacts.
  const buildArcProgress = (arr) => {
    const out = new Float32Array(arr.length)
    let total = 0
    out[0] = 0
    for (let i = 1; i < arr.length; i += 1) {
      total += Math.hypot(arr[i].x - arr[i - 1].x, arr[i].y - arr[i - 1].y)
      out[i] = total
    }
    if (total <= 1e-4) {
      const denom = Math.max(1, arr.length - 1)
      for (let i = 0; i < arr.length; i += 1) out[i] = i / denom
      return out
    }
    for (let i = 1; i < arr.length; i += 1) out[i] /= total
    return out
  }
  const sampleArcPoint = (arr, progress, t) => {
    if (t <= 0) return arr[0]
    if (t >= 1) return arr[arr.length - 1]
    for (let i = 0; i < progress.length - 1; i += 1) {
      const aT = progress[i]
      const bT = progress[i + 1]
      if (t < aT || t > bT) continue
      const span = Math.max(1e-4, bT - aT)
      const k = clamp((t - aT) / span, 0, 1)
      return {
        x: arr[i].x + (arr[i + 1].x - arr[i].x) * k,
        y: arr[i].y + (arr[i + 1].y - arr[i].y) * k
      }
    }
    return arr[arr.length - 1]
  }

  const upperProgress = buildArcProgress(upperChain)
  const lowerProgress = buildArcProgress(lowerChain)
  const close = clamp(Math.pow(a, 0.95), 0, 1)
  const lowerSampled = upperChain.map((u, i) => {
    const t = upperProgress[i]
    return sampleArcPoint(lowerChain, lowerProgress, t)
  })
  const topFront = upperChain.map((u, i) => {
    const l = lowerSampled[i]
    const mx = (u.x + l.x) * 0.5
    const my = (u.y + l.y) * 0.5
    return {
      x: u.x + (mx - u.x) * close,
      y: u.y + (my - u.y) * close
    }
  })
  const bottomFront = lowerSampled.map((l, i) => {
    const u = upperChain[i]
    const mx = (u.x + l.x) * 0.5
    const my = (u.y + l.y) * 0.5
    return {
      x: l.x + (mx - l.x) * close,
      y: l.y + (my - l.y) * close
    }
  })

  ctx.save()
  ctx.beginPath()
  const f = polyPts[0]
  if (!f) {
    ctx.restore()
    return false
  }
  ctx.moveTo(f.x, f.y)
  for (let i = 1; i < polyPts.length; i += 1) {
    const p = polyPts[i]
    ctx.lineTo(p.x, p.y)
  }
  ctx.closePath()
  ctx.clip()

  // Eyelids should be fully opaque and render as the front-most eye layer.
  const prevGlobalAlpha = ctx.globalAlpha
  ctx.globalAlpha = 1
  const oc = 'rgba(0, 0, 0, 1)'

  // For fully closed frames, fill the full socket.
  if (b >= 0.82) {
    ctx.fillStyle = oc
    ctx.beginPath()
    ctx.moveTo(polyPts[0].x, polyPts[0].y)
    for (let i = 1; i < polyPts.length; i += 1) {
      const p = polyPts[i]
      ctx.lineTo(p.x, p.y)
    }
    ctx.closePath()
    ctx.fill()
    ctx.globalAlpha = prevGlobalAlpha
    ctx.restore()
    return true
  }

  ctx.fillStyle = oc
  ctx.beginPath()
  ctx.moveTo(upperChain[0].x, upperChain[0].y)
  for (let i = 1; i < upperChain.length; i += 1) {
    const tp = upperChain[i]
    ctx.lineTo(tp.x, tp.y)
  }
  for (let i = topFront.length - 1; i >= 0; i -= 1) {
    const fp = topFront[i]
    ctx.lineTo(fp.x, fp.y)
  }
  ctx.closePath()
  ctx.fill()

  ctx.fillStyle = oc
  ctx.beginPath()
  ctx.moveTo(lowerSampled[0].x, lowerSampled[0].y)
  for (let i = 1; i < lowerSampled.length; i += 1) {
    const lp = lowerSampled[i]
    ctx.lineTo(lp.x, lp.y)
  }
  for (let i = bottomFront.length - 1; i >= 0; i -= 1) {
    const fp = bottomFront[i]
    ctx.lineTo(fp.x, fp.y)
  }
  ctx.closePath()
  ctx.fill()

  // Seal lines at moving lid edges.
  if (a > 0.2 && topFront.length >= 2) {
    const eyeHeight = Math.max(1, Math.abs(pLo.y - pUp.y))
    const rimW = Math.max(0.6, Math.min(1.4, eyeHeight * 0.13))
    ctx.beginPath()
    ctx.moveTo(topFront[0].x, topFront[0].y)
    for (let i = 1; i < topFront.length; i += 1) {
      const p = topFront[i]
      ctx.lineTo(p.x, p.y)
    }
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.22)'
    ctx.lineJoin = 'round'
    ctx.lineCap = 'round'
    ctx.lineWidth = rimW
    ctx.stroke()

    ctx.beginPath()
    ctx.moveTo(bottomFront[0].x, bottomFront[0].y)
    for (let i = 1; i < bottomFront.length; i += 1) {
      const p = bottomFront[i]
      ctx.lineTo(p.x, p.y)
    }
    ctx.stroke()
  }

  if (a >= 0.9) {
    ctx.fillStyle = oc
    ctx.beginPath()
    const f2 = polyPts[0]
    if (f2) {
      ctx.moveTo(f2.x, f2.y)
      for (let i = 1; i < polyPts.length; i += 1) {
        const p = polyPts[i]
        ctx.lineTo(p.x, p.y)
      }
      ctx.closePath()
      ctx.fill()
    }
  }

  ctx.globalAlpha = prevGlobalAlpha
  ctx.restore()
  return true
}
